import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { requisitionPath } from '../../lib/slug';
import { requisitionsApi, reqStatusMeta, reqPriorityMeta } from './requisitionsApi';
import './requisitionPrint.css';

const date = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function RequisitionPrintPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { properties } = useProperty();

  const [req, setReq] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, us, locs] = await Promise.all([
        requisitionsApi.get(routeRef),
        catalogApi.listUnits().catch(() => []),
        catalogApi.listLocations().catch(() => []),
      ]);
      setReq(r);
      setUnits(us || []);
      setLocations(locs || []);
      const act = await requisitionsApi.listActivity(r.id, { pageSize: 100 }).catch(() => ({ items: [] }));
      setActivity(act?.items || []);
    } catch (err) {
      setError(err?.message || t('Could not load requisition.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;
  const property = properties.find((p) => p.id === req?.propertyId);

  if (loading) {
    return <div className="ps-scope rq-print__loading"><Spinner size={26} /></div>;
  }
  if (error || !req) {
    return (
      <div className="ps-scope rq-print__loading">
        <p>{error || t('Not found.')}</p>
        <button type="button" className="rq-print__btn" onClick={() => navigate('/requisitions')}>{t('Back')}</button>
      </div>
    );
  }

  const statusMeta = reqStatusMeta(req.status);
  const priorityMeta = reqPriorityMeta(req.priority);
  const lines = req.lines || [];
  const history = [...activity].reverse(); // oldest-first for a document trail
  const steps = req.approvalSteps || [];
  const hasChain = steps.length > 0;
  const decidedSteps = steps.filter((s) => s.decidedByName);

  return (
    <div className="ps-scope rq-print">
      <div className="rq-print__toolbar">
        <button type="button" className="rq-print__btn rq-print__btn--ghost" onClick={() => navigate(requisitionPath(req))}>
          <Icon.ArrowLeft size={15} /> {t('Back')}
        </button>
        <button type="button" className="rq-print__btn" onClick={() => window.print()}>
          <Icon.Printer size={15} /> {t('Print / Save as PDF')}
        </button>
      </div>

      <div className="rq-doc" role="document">
        <header className="rq-doc__head">
          <div className="rq-doc__brand">
            <div className="rq-doc__property">{property?.name || t('Property')}</div>
            <div className="rq-doc__subtitle">{t('Purchase Requisition')}</div>
          </div>
          <div className="rq-doc__ref">
            <div className="rq-doc__number">{req.number}</div>
            <div className={`rq-doc__status rq-doc__status--${statusMeta.tone}`}>{t(statusMeta.label)}</div>
          </div>
        </header>

        <h1 className="rq-doc__title">{req.title}</h1>

        <section className="rq-doc__meta">
          <div><span>{t('Requested by')}</span><strong>{req.requestedByName || '—'}</strong></div>
          <div><span>{t('Priority')}</span><strong>{t(priorityMeta.label)}</strong></div>
          <div><span>{t('Deliver to')}</span><strong>{locationName(req.locationId) || '—'}</strong></div>
          <div><span>{t('Needed by')}</span><strong>{date(req.neededByDate)}</strong></div>
          <div><span>{t('Created')}</span><strong>{date(req.createdAt)}</strong></div>
          <div><span>{t('Submitted')}</span><strong>{date(req.submittedAt)}</strong></div>
        </section>

        <table className="rq-doc__lines">
          <thead>
            <tr>
              <th className="rq-doc__c-num">#</th>
              <th>{t('Description')}</th>
              <th className="rq-doc__c-supplier">{t('Supplier')}</th>
              <th className="rq-doc__c-qty">{t('Qty')}</th>
              <th className="rq-doc__c-unit">{t('Unit')}</th>
              <th className="rq-doc__c-money">{t('Unit price')}</th>
              <th className="rq-doc__c-money">{t('Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.lineId || i}>
                <td className="rq-doc__c-num">{i + 1}</td>
                <td>
                  <div className="rq-doc__desc">{l.description}</div>
                  {l.sku && <div className="rq-doc__sku">{l.sku}</div>}
                </td>
                <td className="rq-doc__c-supplier">{l.preferredSupplierName || '—'}</td>
                <td className="rq-doc__c-qty">{l.quantity}</td>
                <td className="rq-doc__c-unit">{uomCode(l.uomId) || '—'}</td>
                <td className="rq-doc__c-money">{money(l.estimatedUnitPrice)}</td>
                <td className="rq-doc__c-money">{money(l.estimatedTotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} className="rq-doc__total-label">{t('Estimated total')}</td>
              <td className="rq-doc__c-money rq-doc__total-value">{money(req.estimatedTotal)}</td>
            </tr>
          </tfoot>
        </table>

        {req.notes && (
          <section className="rq-doc__notes">
            <span className="rq-doc__section-label">{t('Notes')}</span>
            <p>{req.notes}</p>
          </section>
        )}

        {(req.submittedAt || req.decidedByName || decidedSteps.length > 0) && (
          <section className="rq-doc__sign">
            {req.submittedAt && (
              <div className="rq-doc__sign-block">
                <span className="rq-doc__section-label">{t('Requested by')}</span>
                <div className="rq-doc__sign-mark">
                  {req.submittedSignature ? <img src={req.submittedSignature} alt="" /> : null}
                </div>
                <div className="rq-doc__sign-name">{req.requestedByName || '—'}</div>
                <div className="rq-doc__sign-meta">{dateTime(req.submittedAt)}</div>
              </div>
            )}
            {hasChain
              ? decidedSteps.map((s) => (
                <div className="rq-doc__sign-block" key={s.order}>
                  <span className="rq-doc__section-label">
                    {s.status === 'Rejected' ? t('Rejected by') : t('Approved by')} · {s.roleName || t('Step {order}', { order: s.order })}
                  </span>
                  <div className="rq-doc__sign-mark">
                    {s.signature ? <img src={s.signature} alt="" /> : null}
                  </div>
                  <div className="rq-doc__sign-name">{s.decidedByName}</div>
                  <div className="rq-doc__sign-meta">{dateTime(s.decidedAt)}</div>
                </div>
              ))
              : req.decidedByName && (
                <div className="rq-doc__sign-block">
                  <span className="rq-doc__section-label">{req.status === 'Rejected' ? t('Rejected by') : t('Approved by')}</span>
                  <div className="rq-doc__sign-mark">
                    {req.decisionSignature ? <img src={req.decisionSignature} alt="" /> : null}
                  </div>
                  <div className="rq-doc__sign-name">{req.decidedByName}</div>
                  <div className="rq-doc__sign-meta">{dateTime(req.decidedAt)}</div>
                </div>
              )}
          </section>
        )}

        {history.length > 0 && (
          <section className="rq-doc__history">
            <span className="rq-doc__section-label">{t('Audit trail')}</span>
            <ul>
              {history.map((a) => (
                <li key={a.id}>
                  <span className="rq-doc__history-date">{dateTime(a.createdAt)}</span>
                  <span className="rq-doc__history-text">
                    {a.type === 'Commented' ? `${a.actorName || t('Someone')}: “${a.summary}”` : `${a.actorName || t('Someone')} ${a.summary}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="rq-doc__footer">
          <span>{req.number} · {property?.name || ''}</span>
          <span>{t('Generated {time}', { time: dateTime(new Date()) })}</span>
        </footer>
      </div>
    </div>
  );
}
